import { useCallback, useEffect, useState } from 'react';

import { ApiError, api } from '../api/client';
import { ChartFrame } from '../charts/ChartFrame';
import { combineFrames } from '../charts/combine';
import { getTemplate } from '../charts/registry';
import { ChartError, ChartLoading } from '../charts/states';
import type { ChartData, SavedChart } from '../types';
import { CustomHtmlChart } from './CustomHtmlChart';

/**
 * Fetches a chart's data and draws it — the one place that turns a saved chart
 * into pixels, whether it is a template or a generated HTML fragment.
 *
 * `overrideParams` carries what the platform passed in (app_id / app_ids /
 * user_partner_id and anything else on the embed URL), merged over the chart's
 * own saved params.
 */

interface State {
  data: ChartData | null;
  error: string | null;
  /** HTTP status behind `error`, when there was one. 401 means "not signed in". */
  status?: number;
  /** A fetch is in flight and `data` is the previous render, not this one. */
  stale: boolean;
}

export const useChartData = (
  source: string,
  chartId: string,
  params: Record<string, string>,
): State & { retry: () => void } => {
  const [state, setState] = useState<State>({ data: null, error: null, stale: false });
  const [attempt, setAttempt] = useState(0);

  // Params are an object; a stable key keeps the effect from firing on every
  // render just because the caller built a fresh object.
  const paramKey = JSON.stringify(params);

  useEffect(() => {
    if (!chartId) {
      setState({ data: null, error: null, stale: false });
      return;
    }

    let cancelled = false;
    // Keep whatever is on screen and mark it stale. A dashboard changing app
    // filter should dim and settle, not blank out and jump.
    setState((current) => ({ ...current, error: null, stale: true }));

    api
      .query({ source, chartId, params: JSON.parse(paramKey) as Record<string, string> })
      .then((data) => {
        if (!cancelled) setState({ data, error: null, stale: false });
      })
      .catch((err: Error) => {
        // Keep the status, not just the words. A 401 is "you are not signed
        // in", which deserves an explanation; anything else is a fault.
        const status = err instanceof ApiError ? err.status : undefined;
        if (!cancelled) setState({ data: null, error: err.message, status, stale: false });
      });

    return () => {
      cancelled = true;
    };
  }, [source, chartId, paramKey, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  return { ...state, retry };
};

interface RenderState extends State {
  /** The named source frames, for a composite chart's custom-HTML fragment. */
  named: Record<string, ChartData> | null;
}

/**
 * Render-time data for a chart, single-query or composite.
 *
 * A composite chart (`chart.queries`) fetches every named query, then combines
 * them in the browser (charts/combine.ts) — by the chart's `sql` if it has
 * one, else a default join on the shared key. A plain chart takes the single
 * `/api/query` path unchanged. One hook, so the rules-of-hooks order never
 * shifts when a chart happens to have queries.
 */
const useRenderData = (
  chart: SavedChart,
  overrideParams?: Record<string, string>,
): RenderState & { retry: () => void } => {
  const [state, setState] = useState<RenderState>({
    data: null,
    named: null,
    error: null,
    stale: false,
  });
  const [attempt, setAttempt] = useState(0);

  const composite = chart.queries && chart.queries.length > 0 ? chart.queries : null;
  // A stable key over everything the fetch depends on, so the effect re-runs
  // when — and only when — the inputs change.
  const key = JSON.stringify({
    composite,
    source: chart.source,
    chartId: chart.chartId,
    params: chart.params,
    override: overrideParams ?? null,
  });

  useEffect(() => {
    let cancelled = false;
    setState((current) => ({ ...current, error: null, stale: true }));

    const run = async (): Promise<void> => {
      if (composite) {
        const named: Record<string, ChartData> = {};
        for (const q of composite) {
          named[q.key] = await api.query({
            source: q.source,
            chartId: q.chartId,
            params: { ...q.params, ...overrideParams },
          });
        }
        const data = combineFrames(named);
        if (!cancelled) setState({ data, named, error: null, stale: false });
        return;
      }
      if (!chart.chartId) {
        if (!cancelled) setState({ data: null, named: null, error: null, stale: false });
        return;
      }
      const data = await api.query({
        source: chart.source,
        chartId: chart.chartId,
        params: { ...chart.params, ...overrideParams },
      });
      if (!cancelled) setState({ data, named: null, error: null, stale: false });
    };

    run().catch((err: Error) => {
      const status = err instanceof ApiError ? err.status : undefined;
      if (!cancelled) setState({ data: null, named: null, error: err.message, status, stale: false });
    });

    return () => {
      cancelled = true;
    };
    // `key` captures every input the closure reads; listing the objects
    // themselves would re-run on each fresh render for no change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  return { ...state, retry };
};

interface Props {
  chart: SavedChart;
  overrideParams?: Record<string, string>;
  /** Title shown on the card. Defaults to the chart's own name. */
  title?: string;
  /** (i) text for this placement; blank or absent falls back to the chart's own. */
  description?: string;
  /** Widget embeds sit inside the platform's own card, which draws the header. */
  chrome?: boolean;
}

export const ChartRenderer = ({ chart, overrideParams, title, description, chrome = true }: Props) => {
  const { data, named, error, stale, retry } = useRenderData(chart, overrideParams);

  if (error) return <ChartError message={error} onRetry={retry} />;
  if (!data) return <ChartLoading />;

  const Template = getTemplate(chart.template).Component;

  return (
    <ChartFrame
      title={chrome ? (title ?? chart.name) : undefined}
      description={description?.trim() || chart.description}
      data={data}
      chrome={chrome}
      css={chart.css}
      scopeId={chart.id}
    >
      <div className="chart-card__plot" data-stale={stale || undefined}>
        {chart.kind === 'custom' && chart.html ? (
          <CustomHtmlChart html={chart.html} data={data} datasets={named ?? undefined} title={chart.name} />
        ) : (
          <Template data={data} fields={chart.fields} tiles={chart.tiles} datasets={named ?? undefined} />
        )}
      </div>
    </ChartFrame>
  );
};
